import React from 'react';
import styled from 'styled-components';
import { Button } from '@prosolve/common-components-ui'
import prosolveLogo from '../images/prosolve-logo-dark-horizontal.png';
import mazeTile from '../images/maze-tile-seamless.svg';

// Shared look and feel for the auth pages (login, admin imitator login,
// forgot password, reset password), based on the TECH-930 hub_login prototype.

export const starBackgroundUrl = 'https://starautismsupport.com/sites/default/files/SAS%20Background-%20light%20bg-2.png';

export const Page = styled.div`
  height: 100%;
  box-sizing: border-box;
  display: flex;
  padding: 24px;
  position: relative;
  overflow: auto;
  overflow-x: hidden;
  background: ${props => props.$isStar
    ? `url(${starBackgroundUrl}) top left/cover no-repeat fixed`
    : 'linear-gradient(150deg, #30cfe5 0%, #009db7 42%, #007691 74%, #004259 100%)'};
`;

export const MazeOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-color: #ffffff;
  opacity: .08;
  -webkit-mask-image: url(${mazeTile});
  mask-image: url(${mazeTile});
  -webkit-mask-repeat: repeat;
  mask-repeat: repeat;
  -webkit-mask-size: 900px 900px;
  mask-size: 900px 900px;
`;

export const Card = styled.main`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 440px;
  margin: auto;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e6eaf0;
  border-radius: 24px;
  padding: 44px 40px 38px;
  box-shadow: 0 24px 60px -20px rgba(0,66,89,.28), 0 4px 14px rgba(0,19,35,.05);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: #001323;
  @media (max-width: 480px) {
    padding: 36px 24px 30px;
    border-radius: 20px;
  }
`;

const Brand = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 26px;
`;

const Logo = styled.img`
  height: 34px;
  width: auto;
  display: block;
`;

export const BrandLogo = () => (
  <Brand>
    <Logo src={prosolveLogo} alt="ProSolve" />
  </Brand>
);

export const Head = styled.div`
  text-align: center;
  margin-bottom: 30px;
`;

export const Title = styled.h1`
  font-family: 'Raleway', sans-serif;
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -.01em;
  color: #001323;
  margin: 0 0 8px;
  @media (max-width: 480px) {
    font-size: 23px;
  }
`;

export const Subtitle = styled.p`
  font-size: 14.5px;
  color: #4a6577;
  line-height: 1.5;
  max-width: 320px;
  margin: 0 auto;
`;

// Styles the common-components TextInput (and its password eye toggle, which
// carries inline styles — hence the !important overrides) from the outside,
// so its :focus state can still take effect.
export const FieldWrap = styled.div`
  margin-bottom: 18px;
  input {
    font-family: 'Inter', sans-serif;
    font-size: 15px;
    color: #001323;
    background: #f8f9fb;
    border: 1.5px solid ${props => props.$invalid ? '#ef0c0c' : '#d5dce5'};
    border-radius: 12px;
    padding: 13px 15px;
    width: 100%;
    transition: border-color .15s, box-shadow .15s, background .15s;
  }
  input::placeholder {
    color: #8897a8;
  }
  input:focus {
    outline: none;
    background: #ffffff;
    border-color: #009db7;
    box-shadow: 0 0 0 4px rgba(48,207,229,.16);
  }
  input + div > div {
    color: #8897a8 !important;
    font-size: 20px !important;
    top: -34px !important;
    left: -16px !important;
  }
  input + div > div:hover {
    color: #009db7 !important;
  }
`;

export const fieldLabelStyle = {
  fontFamily: "'Raleway', sans-serif",
  fontSize: '11px',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '.09em',
  color: '#4a6577'
};

export const LinkRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: -4px 0 22px;
`;

export const TextLink = styled.span`
  font-family: 'Raleway', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: #009db7;
  cursor: pointer;
  transition: color .15s;
  &:hover {
    color: #007691;
    text-decoration: underline;
  }
`;

export const ErrorSlot = styled.div`
  min-height: 20px;
  margin: -12px 0 6px;
  color: #ef0c0c;
  font-size: 13px;
  font-style: italic;
`;

export const SubmitRow = styled.div`
  position: relative;
`;

export const SubmitButton = styled(Button)`
  width: 100%;
  box-sizing: border-box;
  font-family: 'Raleway', sans-serif;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: .01em;
  color: #ffffff;
  background: #009db7;
  border: none;
  border-radius: 12px;
  padding: 14px;
  transition: background .15s, transform .05s;
  &:hover {
    background: #007691;
    box-shadow: none;
  }
  &:active {
    background: #007691;
    transform: translateY(1px);
  }
`;

export const SpinnerWrap = styled.div`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%) scale(.55);
  line-height: 0;
`;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 24px 0 20px;
  color: #8897a8;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
  &::before, &::after {
    content: "";
    height: 1px;
    background: #e6eaf0;
    flex: 1;
  }
`;

export const Foot = styled.div`
  margin-top: 28px;
  text-align: center;
  font-size: 12.5px;
  color: #8897a8;
  line-height: 1.5;
`;

export const Tagline = styled.div`
  font-family: 'Raleway', sans-serif;
  font-weight: 700;
  color: #4a6577;
  letter-spacing: .02em;
  margin-top: 6px;
`;

export const CrossLink = styled.div`
  margin-top: 18px;
  text-align: center;
  font-size: 14px;
`;
